import React, { Component } from 'react';
import { connect } from 'react-redux';
import numeral from 'numeral';
import RouteTitle from 'components/shared/route-title/RouteTitle';
import DetailsHeader from 'components/shared/details/DetailsHeader';
import './ClientDetails.css';
import DetailsInfo from 'components/shared/details/DetailsInfo';
import DetailsInfoItem from 'components/shared/details/DetailsInfoItem';
import NavigationBar from 'components/shared/navigation/NavigationBar';
import {
  getBuildings,
  getCurrentClient,
  getCurrentFilter,
  getCurrentPage,
  getInvoices,
  getPurchaseOrders,
  getShowContactsModal,
  getSurveys,
  getUsers,
  getWorkOrders,
  getProjects,
} from 'selectors/admin/clients/clients';
import {
  changeFilter,
  clearClientForm,
  loadClientRequest,
  loadInvoicesRequest,
  loadPurchaseOrdersRequest,
  loadSurveysRequest,
  loadWorkOrdersRequest,
  setShowInviteUserClientModal,
  showContactsModal,
  showContactsRequest,
  goToEditClient,
  loadProjectsRequest,
} from 'actions/admin/clients/clients';
import Number from 'components/shared/text/Number';
import SimpleTable from 'components/shared/table/SimpleTable';
import SimplePagination from 'components/shared/pagination/SimplePagination';
import { WorkOrderStatusEnum } from 'services/utils/work-order-status-enum';
import Date from 'components/shared/text/Date';
import {
  formatPhoneNumber,
  toUpperFirstLetter,
} from 'services/utils/text-util';
import { goToInvoice } from 'actions/admin/invoices/invoices';
import SimpleHeader from 'components/shared/table/SimpleHeader';
import ContactsModal from 'components/shared/modal/ContactsModal';
import { Client } from 'domain/client';
import Button from 'components/shared/button/Button';
import ClientInviteUserModal from 'components/admin/clients/ClientInviteUserModal';
import { getCurrentUser } from 'selectors/authentication';
import { PermissionsUtil } from 'services/utils/permissions-util';
import { ROLES, usersUtil } from 'services/utils/users-util';
import DocumentLink from 'components/shared/document/DocumentLink';
import SurveysTable from 'components/admin/surveys/SurveysTable';
import SurveyRating from 'components/admin/surveys/SurveyRating';
import { goToSurvey } from 'components/admin/surveys/store/actions';
import ClientDetailsNoteEditor from 'components/admin/clients/ClientDetailsNoteEditor';
import LogoUpload from 'components/shared/logo-upload/LogoUpload';
import ClientDetailsWorkOrders from 'components/admin/clients/ClientDetailsWorkOrders';
import ClientDetailsProjects from 'components/admin/clients/ClientDetailsProjects';

class ClientDetails extends Component {
  constructor(props) {
    super(props);
    // Keep track of id to avoid showing an "old" client while loading the current one
    this.clientId = numeral(props.match.params.id).value();
    this.canSeeMoney = PermissionsUtil.adminUserCanSeeMoney(props.currentUser);
    this.userIsSpackleAdmin = usersUtil.hasRole(
      props.currentUser,
      ROLES.SPACKLE_ADMIN
    );
    this.userIsAdmin = usersUtil.isAdmin(props.currentUser);
    this.currentNav = this.userIsAdmin ? 1 : 5;
  }

  componentDidMount() {
    this.props.dispatch(loadClientRequest(this.clientId));

    if (this.currentNav === 1) {
      this.props.dispatch(loadWorkOrdersRequest(this.clientId, 1));
    } else {
      this.props.dispatch(loadProjectsRequest(this.clientId, 1));
    }
  }

  handleInvoiceSelection = (index) => {
    this.props.dispatch(goToInvoice(this.props.invoices.content[index].id));
  };

  handleNavSelect = (value) => {
    const { currentClient, dispatch } = this.props;
    this.currentNav = value;
    switch (value) {
      case 1:
        dispatch(loadWorkOrdersRequest(currentClient.id, 1)).then(
          (response) => {
            if (response.ok) {
              dispatch(changeFilter('all'));
            }
          }
        );
        break;

      case 2:
        dispatch(loadInvoicesRequest(currentClient.id, 1)).then((response) => {
          if (response.ok) {
            dispatch(changeFilter('all'));
          }
        });
        break;

      case 3:
        dispatch(loadPurchaseOrdersRequest(currentClient.id, 1));
        break;

      case 4:
        dispatch(loadSurveysRequest(currentClient.id, 1));
        break;

      case 5:
        dispatch(loadProjectsRequest(currentClient.id, 1));
        break;

      default:
    }
  };

  handlePageChange = (newPage) => {
    // Take into account if the "pending" section is selected
    const { currentFilter, currentClient, dispatch } = this.props;
    switch (this.currentNav) {
      case 2:
        dispatch(loadInvoicesRequest(currentClient.id, newPage, currentFilter));
        break;

      case 3:
        dispatch(loadPurchaseOrdersRequest(currentClient.id, newPage));
        break;

      case 4:
        dispatch(loadSurveysRequest(currentClient.id, newPage));
        break;

      default:
    }
  };

  handleFilterSelect = (filter) => {
    const status = WorkOrderStatusEnum.create(filter);
    if (!WorkOrderStatusEnum.UNKNOWN.equals(status)) {
      this.props
        .dispatch(loadWorkOrdersRequest(this.props.currentClient.id, 1, status))
        .then((response) => {
          if (response.ok) {
            this.props.dispatch(changeFilter(filter));
          }
        });
    } else {
      // If status is unknow is because filter is "all"
      this.props
        .dispatch(loadWorkOrdersRequest(this.props.currentClient.id, 1))
        .then((response) => {
          if (response.ok) {
            this.props.dispatch(changeFilter(filter));
          }
        });
    }
  };

  handleInvoicesFilterSelect = (filter) => {
    this.props
      .dispatch(loadInvoicesRequest(this.props.currentClient.id, 1, filter))
      .then((response) => {
        if (response.ok) {
          this.props.dispatch(changeFilter(filter));
        }
      });
  };

  handleViewAll = (event) => {
    event.preventDefault();
    this.props.dispatch(
      showContactsRequest(
        this.props.currentClient.id,
        this.props.users,
        this.props.buildings
      )
    );
  };

  handleContactsModalClose = () => {
    this.props.dispatch(showContactsModal(false));
  };

  handleInviteUserClick = () => {
    this.props.dispatch(clearClientForm());
    this.props.dispatch(setShowInviteUserClientModal(true));
  };

  handleEdit = () => {
    this.props.dispatch(goToEditClient(this.props.currentClient.id));
  };

  render() {
    const { currentClient } = this.props;
    let rightHeaders = [];
    if (currentClient !== null && this.userIsAdmin) {
      rightHeaders = [
        [
          {
            title: 'WO COMPLETED',
            value: currentClient.wo_complete_count,
          },
          {
            title: 'AVERAGE RATING',
            value: (
              <SurveyRating
                rating={numeral(currentClient.avg_rating).value()}
                showWaiting={false}
              />
            ),
          },
          {
            title: 'TOTAL SPENT',
            value:
              currentClient.total_spent !== null ? (
                <Number value={currentClient.total_spent} format="$0,0" />
              ) : (
                'N/A'
              ),
          },
          {
            title: 'AVG VARIANCE',
            value: Client.getVariance(currentClient, 'N/A'),
          },
        ],
      ];
      if (
        currentClient.purchase_order !== null &&
        currentClient.purchase_order !== undefined
      ) {
        rightHeaders.push([
          {
            title: 'CURRENT PO #',
            value: currentClient.purchase_order.number,
          },
          {
            title: 'AMOUNT',
            value: (
              <Number
                value={currentClient.purchase_order.amount}
                format="$0,0"
              />
            ),
          },
          {
            title: 'BALANCE',
            value: (
              <Number
                value={currentClient.purchase_order.balance}
                format="$0,0"
              />
            ),
          },
          {
            title: '',
            value: '',
          },
        ]);
      }
    }
    return (
      <div className="client-details">
        {currentClient !== null && currentClient.id === this.clientId && (
          <React.Fragment>
            <RouteTitle
              titles={['CLIENTS', currentClient.company_name.toUpperCase()]}
            />

            <DetailsHeader
              leftHeader={{
                title: 'CLIENT',
                value: currentClient.company_name,
              }}
              leftHeaderExtraComponents={
                currentClient.logo &&
                currentClient.logo.image && (
                  <div className="logo-preview-container">
                    <LogoUpload
                      extensions={['.jpg', '.jpeg', '.gif', '.png']}
                      logos={[
                        {
                          preview: currentClient.logo.image.url,
                          url: currentClient.logo.image.url,
                        },
                      ]}
                      readOnly={true}
                    />
                  </div>
                )
              }
              rightHeaders={rightHeaders}
            />

            <div className="details-info-container">
              <div className="edit-button-container">
                <input
                  type="button"
                  className="btn button button-gray"
                  value="Edit"
                  onClick={this.handleEdit}
                />
              </div>

              <DetailsInfo
                itemsContainerSize={8}
                itemsLines={[
                  [
                    <DetailsInfoItem
                      lines={
                        currentClient.location !== null
                          ? [
                              currentClient.location.street_address_1,
                              `${currentClient.location.city}, ${currentClient.location.state} ${currentClient.location.zip}`,
                            ]
                          : ['N/A']
                      }
                      title="ADDRESS"
                    />,
                    <DetailsInfoItem
                      lines={[
                        <span className="name">
                          {`${currentClient.contact.first_name} ${currentClient.contact.last_name}`}
                        </span>,
                        <span className="email">
                          {currentClient.contact.email}
                        </span>,
                        <span>
                          {formatPhoneNumber(
                            currentClient.contact.phone_number
                          )}
                        </span>,
                      ]}
                      title={
                        <div>
                          <span>PRIMARY CONTACT</span>
                          <span
                            className="view-all"
                            onClick={this.handleViewAll}
                          >
                            View All
                          </span>
                        </div>
                      }
                    />,
                    <DetailsInfoItem
                      lines={currentClient.propietary_sub_contractors.map(
                        (element) => {
                          return <li className="contractor">{element.name}</li>;
                        }
                      )}
                      title="PROPRIETARY SUBCONTRACTORS"
                    />,
                  ],
                ]}
                rightContainer={
                  this.userIsAdmin && (
                    <div className="note-editor-container">
                      <ClientDetailsNoteEditor />
                    </div>
                  )
                }
                rightContainerSize={2}
              />
            </div>

            <div className="content-container">
              <React.Fragment>
                <NavigationBar
                  items={[
                    {
                      id: 1,
                      title: 'Work Orders',
                      show: this.userIsAdmin,
                    },
                    {
                      id: 2,
                      title: 'Invoices',
                      show: this.userIsAdmin && this.canSeeMoney,
                    },
                    {
                      id: 3,
                      title: 'Purchase Orders',
                      show: this.userIsAdmin,
                    },
                    {
                      id: 4,
                      title: 'Surveys',
                      show: this.userIsAdmin,
                    },
                    {
                      id: 5,
                      title: 'Projects',
                      class: this.currentNav === 5 ? 'selected' : '',
                    },
                  ]}
                  onSelected={this.handleNavSelect}
                />

                <div className="navigation-content">
                  {this.currentNav === 1 && <ClientDetailsWorkOrders />}
                  {this.currentNav === 2 && this.props.invoices !== null && (
                    <div>
                      <SimpleTable
                        className="invoices-table"
                        headers={[
                          'WO #',
                          <SimpleHeader
                            label="STATUS"
                            filter={{
                              options: [
                                {
                                  id: 'all',
                                  label: 'All',
                                },
                                {
                                  id: 'paid',
                                  label: 'Paid',
                                },
                                {
                                  id: 'overdue',
                                  label: 'Overdue',
                                },
                              ],
                              selected: this.props.currentFilter,
                            }}
                            onFilterOptionSelect={
                              this.handleInvoicesFilterSelect
                            }
                          />,
                          'DUE DATE',
                          'AMOUNT DUE',
                        ]}
                        rows={this.mapInvoices(this.props.invoices.content)}
                        emptyMessage="No Invoices"
                        onRowSelected={this.handleInvoiceSelection}
                      />

                      <div className="pagination-container">
                        <SimplePagination
                          currentPage={this.props.currentPage}
                          pageSize={this.props.invoices.pageSize}
                          total={this.props.invoices.total}
                          onPageChange={this.handlePageChange}
                        />
                      </div>
                    </div>
                  )}
                  {this.currentNav === 3 && this.props.purchaseOrders !== null && (
                    <div>
                      <SimpleTable
                        className="purchase-orders-table"
                        headers={['PO #', 'AMOUNT', 'BALANCE', 'DOCUMENT']}
                        rows={this.mapPurchaseOrders(
                          this.props.purchaseOrders.content
                        )}
                        emptyMessage="No Purchase Orders"
                      />

                      <div className="pagination-container">
                        <SimplePagination
                          currentPage={this.props.currentPage}
                          pageSize={this.props.purchaseOrders.pageSize}
                          total={this.props.purchaseOrders.total}
                          onPageChange={this.handlePageChange}
                        />
                      </div>
                    </div>
                  )}
                  {this.currentNav === 4 && this.props.surveys !== null && (
                    <div className="surveys-table-container">
                      <SurveysTable
                        showClient={false}
                        surveys={this.props.surveys.content}
                        onRowSelected={(index) =>
                          this.props.dispatch(
                            goToSurvey(this.props.surveys.content[index].id)
                          )
                        }
                      />

                      <div className="pagination-container">
                        <SimplePagination
                          currentPage={this.props.currentPage}
                          pageSize={this.props.surveys.pageSize}
                          total={this.props.surveys.total}
                          onPageChange={this.handlePageChange}
                        />
                      </div>
                    </div>
                  )}
                  {this.currentNav === 5 && <ClientDetailsProjects />}
                </div>
              </React.Fragment>
            </div>
          </React.Fragment>
        )}

        <ContactsModal
          footer={
            this.userIsSpackleAdmin ? (
              <div className="contacts-button-container">
                <Button
                  label="Invite User"
                  className="button-orange"
                  onClick={this.handleInviteUserClick}
                />
              </div>
            ) : null
          }
          show={this.props.showContactsModal}
          users={this.mapUsers(this.props.users)}
          onClose={this.handleContactsModalClose}
        />

        <ClientInviteUserModal />
      </div>
    );
  }

  mapInvoices = (invoices) => {
    return invoices.map((invoice) => {
      return [
        <span className="h4">{invoice.work_order.number}</span>,
        toUpperFirstLetter(invoice.status),
        <Date value={invoice.due_date} format="LL" />,
        <Number value={invoice.cost} format="$0,0" />,
      ];
    });
  };

  mapPurchaseOrders = (purchaseOrders) => {
    return purchaseOrders.map((purchaseOrder) => [
      purchaseOrder.number,
      <Number value={purchaseOrder.amount} format="$0,0" />,
      <Number value={purchaseOrder.balance} format="$0,0" />,
      purchaseOrder.document !== null ? (
        <DocumentLink
          showIcon={false}
          showLink={true}
          url={purchaseOrder.document.document.url}
        />
      ) : (
        'N/A'
      ),
    ]);
  };

  mapUsers = (users) => {
    return users.map((user) => {
      return {
        email: user.email,
        lastSignIn: user.current_sign_in_at,
        name: `${user.first_name} ${user.last_name}`,
        phoneNumber: user.phone_number,
        primaryContact: user.account_creator,
        title: user.title,
      };
    });
  };
}

export default connect((state) => {
  return {
    buildings: getBuildings(state),
    currentClient: getCurrentClient(state),
    currentFilter: getCurrentFilter(state),
    currentUser: getCurrentUser(state),
    currentPage: getCurrentPage(state),
    invoices: getInvoices(state),
    purchaseOrders: getPurchaseOrders(state),
    showContactsModal: getShowContactsModal(state),
    surveys: getSurveys(state),
    users: getUsers(state),
    workOrders: getWorkOrders(state),
    projects: getProjects(state),
  };
})(ClientDetails);



// WEBPACK FOOTER //
// ./src/components/admin/clients/ClientDetails.js